import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { VendorStatusPill } from '@/components/VendorStatusPill';
import { getDocument, getDocumentDownloadUrl, reprocessDocument, approveExtraction } from '@/lib/api';
import { API_BASE } from '@/lib/api';
import {
  ArrowLeft, FileText, RefreshCw, CheckCircle2, AlertCircle, Loader2,
  Download, Eye, Shield, Calendar, Building2, DollarSign, Hash, Save,
  ZoomIn, ZoomOut, Maximize2, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const statusConfig = {
  completed: { label: 'Completed', variant: 'compliant' },
  approved: { label: 'Approved', variant: 'compliant' },
  queued: { label: 'Queued', variant: 'pending' },
  processing: { label: 'Processing', variant: 'info' },
  review_required: { label: 'Review Required', variant: 'warning' },
  failed: { label: 'Failed', variant: 'expired' },
  uploaded: { label: 'Uploaded', variant: 'pending' },
};

export default function DocumentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewUrl, setPreviewUrl] = useState('');
  const [reprocessing, setReprocessing] = useState(false);
  const [approving, setApproving] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [zoom, setZoom] = useState(100);

  const load = async () => {
    try {
      const { data } = await getDocument(id);
      setDoc(data);
      // Initialize edited data from extraction
      const ext = data.extraction_data || data.extracted_fields || {};
      setEditedData(ext);
      // Get preview URL — use the /file endpoint for same-origin PDF loading
      try {
        const token = localStorage.getItem('vs_token');
        const fileUrl = `${API_BASE}/documents/${id}/file?token=${token}`;
        // The /file endpoint handles S3 redirect server-side
        setPreviewUrl(fileUrl);
      } catch {
        const token = localStorage.getItem('vs_token');
        setPreviewUrl(`${API_BASE}/documents/${id}/file?token=${token}`);
      }
    } catch { toast.error('Failed to load document'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  const handleReprocess = async () => {
    setReprocessing(true);
    try {
      const { data: result } = await reprocessDocument(id);
      
      if (result.status === 'completed') {
        // Simulated extraction completed synchronously
        toast.success('Extraction completed');
        load(); // Reload document data
        setReprocessing(false);
      } else {
        // Real extraction queued — poll for completion
        toast.success('Document queued for AI processing...');
        const interval = setInterval(async () => {
          try {
            const { data } = await getDocument(id);
            if (data.extraction_status === 'completed' || data.extraction_status === 'failed') {
              clearInterval(interval);
              setDoc(data);
              setEditedData(data.extraction_data || data.extracted_fields || {});
              setReprocessing(false);
              if (data.extraction_status === 'completed') toast.success('Extraction completed');
              else toast.error('Extraction failed');
            }
          } catch { clearInterval(interval); setReprocessing(false); }
        }, 3000);
        setTimeout(() => { clearInterval(interval); setReprocessing(false); }, 60000);
      }
    } catch { toast.error('Failed to reprocess'); setReprocessing(false); }
  };

  const handleApprove = async () => {
    setApproving(true);
    try {
      const { data } = await approveExtraction(id, { extraction_data: editedData });
      toast.success('Extraction approved — compliance updated');
      load();
    } catch { toast.error('Failed to approve'); }
    finally { setApproving(false); }
  };

  // Auto-save edits with debounce
  const saveTimeoutRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [hasUnsaved, setHasUnsaved] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await approveExtraction(id, { extraction_data: editedData });
      toast.success('Changes saved — compliance re-evaluated');
      setHasUnsaved(false);
      load();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const updateField = (path, value) => {
    setHasUnsaved(true);
    setEditedData(prev => {
      const parts = path.split('.');
      const newData = { ...prev };
      let obj = newData;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!obj[parts[i]] || typeof obj[parts[i]] !== 'object') obj[parts[i]] = {};
        obj[parts[i]] = { ...obj[parts[i]] };
        obj = obj[parts[i]];
      }
      obj[parts[parts.length - 1]] = value;
      return newData;
    });
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-[600px] rounded-xl" /></div>;
  if (!doc) return <p className="text-muted-foreground">Document not found</p>;

  const extStatus = doc.document_state || doc.extraction_status || doc.status;
  const compStatus = editedData.compliance_status || doc.extraction_data?.compliance_status;
  const effectiveStatus = compStatus === 'expired' ? 'expired' : compStatus === 'review_required' ? 'review_required' : extStatus;
  const statusInfo = statusConfig[effectiveStatus] || statusConfig[extStatus] || statusConfig.uploaded;
  const isProcessing = extStatus === 'queued' || extStatus === 'processing' || reprocessing;
  const hasExtraction = !!(doc.extraction_data && Object.keys(doc.extraction_data).length > 0) || !!(doc.extracted_fields && Object.keys(doc.extracted_fields).length > 0);
  const coverage = editedData.coverage_limits || {};

  return (
    <div data-testid="document-detail-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/documents')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{doc.filename || 'Untitled Document'}</h1>
              <VendorStatusPill status={statusInfo.variant} />
            </div>
            <p className="text-sm text-muted-foreground">Vendor: {doc.vendor_name} | Type: {doc.document_type?.toUpperCase()}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {doc.local_path || doc.file_key ? (
            <Button variant="outline" size="sm" data-testid="doc-reprocess-button" onClick={handleReprocess} disabled={isProcessing}>
              {isProcessing ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <RefreshCw className="mr-2 h-3 w-3" />}
              {isProcessing ? 'Processing...' : 'Reprocess'}
            </Button>
          ) : null}
          {hasExtraction && hasUnsaved && (
            <Button variant="outline" size="sm" data-testid="doc-save-button" onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <Save className="mr-2 h-3 w-3" />}
              Save Changes
            </Button>
          )}
          {hasExtraction && extStatus !== 'approved' && (
            <Button data-testid="doc-approve-button" onClick={handleApprove} disabled={approving}>
              {approving ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <CheckCircle2 className="mr-2 h-3 w-3" />}
              Approve Extraction
            </Button>
          )}
          {hasExtraction && extStatus === 'approved' && hasUnsaved && (
            <Button data-testid="doc-save-button" onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <Save className="mr-2 h-3 w-3" />}
              Save & Re-evaluate
            </Button>
          )}
        </div>
      </div>

      {/* AI Disclaimer Banner */}
      <div data-testid="ai-disclaimer-banner" className="flex items-center gap-3 p-3 rounded-lg border border-[hsl(36_80%_76%)] bg-[hsl(36_100%_96%)]">
        <AlertCircle className="h-5 w-5 text-[hsl(28,95%,26%)] shrink-0" />
        <p className="text-sm text-[hsl(28,95%,26%)]">AI-generated summary. Please cross-reference with the original PDF before approving.</p>
      </div>

      {/* Split Screen */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: PDF Preview */}
        <Card className="rounded-xl border shadow-[var(--shadow-soft)] overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Eye className="h-4 w-4" /> Document Preview
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {previewUrl ? (
              <div className="h-[600px] bg-muted relative">
                {/* Render based on file type */}
                {doc.filename?.toLowerCase().endsWith('.pdf') ? (
                  <iframe
                    src={previewUrl}
                    data-testid="doc-preview-iframe"
                    className="w-full h-full border-0"
                    title="Document Preview"
                    sandbox="allow-scripts allow-same-origin"
                    style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'top left', width: `${10000 / zoom}%`, height: `${10000 / zoom}%` }}
                  />
                ) : (doc.filename?.toLowerCase().match(/\.(png|jpg|jpeg|tiff)$/)) ? (
                  <div className="w-full h-full overflow-auto flex items-center justify-center p-4">
                    <img
                      src={previewUrl}
                      alt={doc.filename}
                      data-testid="doc-preview-image"
                      className="object-contain"
                      style={{ maxWidth: `${zoom}%`, maxHeight: `${zoom}%` }}
                    />
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center">
                      <FileText className="mx-auto h-12 w-12 text-muted-foreground/40 mb-4" />
                      <p className="text-muted-foreground text-sm">Preview not available for this file type</p>
                      <p className="text-xs text-muted-foreground mt-1">{doc.filename}</p>
                    </div>
                  </div>
                )}
                {/* Control buttons */}
                <div className="absolute bottom-3 right-3 flex gap-2">
                  <div className="flex gap-1 bg-background/95 backdrop-blur-sm border rounded-lg p-1 shadow">
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setZoom(Math.max(50, zoom - 25))} title="Zoom Out">
                      <ZoomOut className="h-3 w-3" />
                    </Button>
                    <div className="flex items-center px-2 text-xs font-medium text-muted-foreground min-w-[45px] justify-center">
                      {zoom}%
                    </div>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setZoom(Math.min(200, zoom + 25))} title="Zoom In">
                      <ZoomIn className="h-3 w-3" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setZoom(100)} title="Reset Zoom">
                      <Maximize2 className="h-3 w-3" />
                    </Button>
                  </div>
                  <Button variant="secondary" size="sm" className="shadow" onClick={() => window.open(previewUrl, '_blank')}>
                    <ExternalLink className="mr-1 h-3 w-3" /> Open
                  </Button>
                  <Button variant="secondary" size="sm" className="shadow" onClick={async () => {
                    try {
                      const { data: u } = await getDocumentDownloadUrl(id);
                      if (u.url) window.open(u.url, '_blank');
                    } catch {}
                  }}>
                    <Download className="mr-1 h-3 w-3" /> Download
                  </Button>
                </div>
              </div>
            ) : (
              <div className="h-[600px] flex items-center justify-center bg-muted">
                <div className="text-center">
                  <FileText className="mx-auto h-12 w-12 text-muted-foreground/40 mb-4" />
                  <p className="text-muted-foreground text-sm">No preview available</p>
                  <p className="text-xs text-muted-foreground mt-1">Upload a file to enable preview</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right: Extraction Data */}
        <Card className="rounded-xl border shadow-[var(--shadow-soft)]">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Shield className="h-4 w-4" /> Extracted Metadata
              </CardTitle>
              {editedData.confidence_score && (
                <Badge variant="secondary" className="text-xs tabular-nums">
                  Confidence: {(editedData.confidence_score * 100).toFixed(0)}%
                </Badge>
              )}
            </div>
            {editedData.summary && <CardDescription className="mt-1">{editedData.summary}</CardDescription>}
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[560px] pr-4">
              {isProcessing ? (
                <div className="py-16 text-center">
                  <Loader2 className="mx-auto h-10 w-10 text-primary animate-spin mb-4" />
                  <p className="text-sm font-medium">AI is analyzing your document...</p>
                  <p className="text-xs text-muted-foreground mt-1">This usually takes 10-30 seconds</p>
                </div>
              ) : !hasExtraction && extStatus === 'failed' ? (
                <div className="py-16 text-center">
                  <AlertCircle className="mx-auto h-10 w-10 text-destructive mb-4" />
                  <p className="text-sm font-medium">Extraction Failed</p>
                  <p className="text-xs text-muted-foreground mt-2">{doc.extraction_error || 'Unknown error'}</p>
                  <Button variant="outline" size="sm" className="mt-4" onClick={handleReprocess}>Retry</Button>
                </div>
              ) : hasExtraction ? (
                <Tabs defaultValue="details" className="space-y-4">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="details">Details</TabsTrigger>
                    <TabsTrigger value="coverage">Coverage</TabsTrigger>
                    <TabsTrigger value="compliance">Compliance</TabsTrigger>
                  </TabsList>

                  <TabsContent value="details" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-muted-foreground">Document Type</Label>
                        <Select value={editedData.document_type || ''} onValueChange={v => updateField('document_type', v)}>
                          <SelectTrigger className="mt-1" data-testid="extraction-doc-type"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {['coi','w9','license','workers_comp','auto_insurance','umbrella','bond','other'].map(t =>
                              <SelectItem key={t} value={t}>{t.toUpperCase().replace('_', ' ')}</SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Policy Number</Label>
                        <Input className="mt-1" data-testid="extraction-policy-number" value={editedData.policy_number || ''} onChange={e => updateField('policy_number', e.target.value)} />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Insured Name</Label>
                      <Input className="mt-1" data-testid="extraction-insured-name" value={editedData.insured_name || ''} onChange={e => updateField('insured_name', e.target.value)} />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Insurance Provider</Label>
                      <Input className="mt-1" data-testid="extraction-provider" value={editedData.insurance_provider || ''} onChange={e => updateField('insurance_provider', e.target.value)} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-muted-foreground">Effective Date</Label>
                        <Input className="mt-1" type="date" data-testid="extraction-effective-date" value={editedData.effective_date || ''} onChange={e => updateField('effective_date', e.target.value)} />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Expiration Date</Label>
                        <Input className="mt-1" type="date" data-testid="extraction-expiration-date" value={editedData.expiration_date || ''} onChange={e => updateField('expiration_date', e.target.value)} />
                      </div>
                    </div>
                    {editedData.tax_id && (
                      <div className="grid grid-cols-2 gap-4">
                        <div><Label className="text-xs text-muted-foreground">Tax ID</Label><Input className="mt-1" value={editedData.tax_id || ''} onChange={e => updateField('tax_id', e.target.value)} /></div>
                        <div><Label className="text-xs text-muted-foreground">Business Type</Label><Input className="mt-1" value={editedData.business_type || ''} onChange={e => updateField('business_type', e.target.value)} /></div>
                      </div>
                    )}
                    {editedData.license_number && (
                      <div className="grid grid-cols-2 gap-4">
                        <div><Label className="text-xs text-muted-foreground">License Number</Label><Input className="mt-1" value={editedData.license_number || ''} onChange={e => updateField('license_number', e.target.value)} /></div>
                        <div><Label className="text-xs text-muted-foreground">State</Label><Input className="mt-1" value={editedData.state_of_issue || ''} onChange={e => updateField('state_of_issue', e.target.value)} /></div>
                      </div>
                    )}
                    {editedData.certificate_holder && (
                      <div><Label className="text-xs text-muted-foreground">Certificate Holder</Label><Input className="mt-1" value={editedData.certificate_holder || ''} onChange={e => updateField('certificate_holder', e.target.value)} /></div>
                    )}
                  </TabsContent>

                  <TabsContent value="coverage" className="space-y-4">
                    <p className="text-xs text-muted-foreground mb-2">Coverage limits (in USD)</p>
                    {[
                      { key: 'general_liability', label: 'General Liability', icon: Shield },
                      { key: 'each_occurrence', label: 'Each Occurrence', icon: DollarSign },
                      { key: 'aggregate', label: 'Aggregate', icon: DollarSign },
                      { key: 'workers_compensation', label: 'Workers Compensation', icon: Shield },
                      { key: 'auto_liability', label: 'Auto Liability', icon: Shield },
                      { key: 'umbrella', label: 'Umbrella', icon: Shield },
                      { key: 'professional_liability', label: 'Professional Liability', icon: Shield },
                    ].map(item => (
                      <div key={item.key} className="flex items-center gap-3">
                        <item.icon className="h-4 w-4 text-muted-foreground shrink-0" />
                        <div className="flex-1">
                          <Label className="text-xs text-muted-foreground">{item.label}</Label>
                          <Input className="mt-1" type="number" placeholder="0" value={coverage[item.key] || ''}
                            onChange={e => updateField(`coverage_limits.${item.key}`, e.target.value ? Number(e.target.value) : null)} />
                        </div>
                      </div>
                    ))}
                  </TabsContent>

                  <TabsContent value="compliance" className="space-y-4">
                    <div>
                      <Label className="text-xs text-muted-foreground">Compliance Status</Label>
                      <Select value={editedData.compliance_status || 'review_required'} onValueChange={v => updateField('compliance_status', v)}>
                        <SelectTrigger className="mt-1" data-testid="extraction-compliance-status"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="compliant">Compliant</SelectItem>
                          <SelectItem value="warning">Warning</SelectItem>
                          <SelectItem value="expired">Expired</SelectItem>
                          <SelectItem value="review_required">Review Required</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {editedData.compliance_reasons?.length > 0 && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Compliance Concerns</Label>
                        <div className="mt-2 space-y-2">
                          {editedData.compliance_reasons.map((reason, i) => (
                            <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-muted/50">
                              <AlertCircle className="h-4 w-4 text-[hsl(36,90%,50%)] shrink-0 mt-0.5" />
                              <span className="text-sm">{reason}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {editedData.additional_insured?.length > 0 && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Additional Insured</Label>
                        <div className="mt-2 space-y-1">
                          {editedData.additional_insured.map((name, i) => (
                            <Badge key={i} variant="secondary" className="mr-1 text-xs">{name}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    <Separator />
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div><span className="text-muted-foreground">AI Model:</span> <span className="font-mono text-xs">{editedData.llm_model || 'gpt-4o'}</span></div>
                      <div><span className="text-muted-foreground">Confidence:</span> <span className="tabular-nums">{editedData.confidence_score ? `${(editedData.confidence_score * 100).toFixed(0)}%` : '-'}</span></div>
                      <div><span className="text-muted-foreground">Extracted:</span> <span className="text-xs">{editedData.extracted_at ? new Date(editedData.extracted_at).toLocaleString() : '-'}</span></div>
                      {editedData.approved_at && <div><span className="text-muted-foreground">Approved:</span> <span className="text-xs">{new Date(editedData.approved_at).toLocaleString()}</span></div>}
                    </div>
                  </TabsContent>
                </Tabs>
              ) : (
                <div className="py-16 text-center">
                  <FileText className="mx-auto h-10 w-10 text-muted-foreground/40 mb-4" />
                  <p className="text-sm text-muted-foreground">No extraction data yet</p>
                  <p className="text-xs text-muted-foreground mt-1">Upload a file or trigger extraction</p>
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
